import { FC } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Code,
  Text,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';

interface JobErrorResult {
  title?: string | null;
  message?: string;
}

interface JobError {
  id?: string;
  created_at?: string;
  finished_at?: string;
  type?: string;
  status?: string;
  result?: JobErrorResult;
}

interface Props {
  error: Error;
}

export const AnvaJobErrorAlert: FC<Props> = ({ error }) => {
  const { t } = useTranslation();

  // Try to parse the job error from the error message
  let jobError: JobError | null = null;
  const errorMessage = error?.message || t('FLOWS_ANVA_UNKNOWN_ERROR');

  try {
    jobError = JSON.parse(error?.message) as JobError;
  } catch {
    // If parsing fails, use the raw error message
  }

  const formatErrorDisplay = () => {
    if (jobError?.result?.message) {
      return jobError.result.message;
    }
    return errorMessage;
  };

  const formatJobDetails = () => {
    if (!jobError) return null;

    return {
      jobId: jobError.id,
      status: jobError.status,
      type: jobError.type,
      createdAt: jobError.created_at,
      finishedAt: jobError.finished_at,
      errorMessage: jobError.result?.message,
      title: jobError.result?.title,
    };
  };

  const jobDetails = formatJobDetails();

  return (
    <Alert status="error" mb="3" flexDirection="column">
      <Box display="flex">
        <AlertIcon alignSelf="flex-start" />
        <Box>
          <AlertTitle>{t('Something went wrong')}</AlertTitle>
        </Box>
      </Box>

      {jobError && (
        <VStack align="stretch" spacing="3">
          <Text fontSize="sm" color="gray.600">
            {t('FLOWS_ANVA_ERROR_DETAILS')}
          </Text>
          <Code
            p="3"
            overflow="auto"
            maxW="full"
            bg="white"
            border="1px"
            borderColor="gray.50"
            textAlign="left"
            whiteSpace="pre-wrap"
          >
            {formatErrorDisplay()}
          </Code>

          {jobDetails && (
            <>
              <Text fontSize="sm" color="gray.600" mt="2">
                {t('FLOWS_ANVA_JOB_INFORMATION')}
              </Text>
              <Code
                p="3"
                overflow="auto"
                maxW="full"
                bg="gray.50"
                border="1px"
                borderColor="gray.200"
                textAlign="left"
                whiteSpace="pre"
                fontSize="xs"
              >
                {JSON.stringify(jobDetails, null, 2)}
              </Code>
            </>
          )}
        </VStack>
      )}

      {!jobError && (
        <Code
          p="3"
          overflow="auto"
          maxW="full"
          bg="white"
          border="1px"
          borderColor="gray.50"
          textAlign="left"
          whiteSpace="pre"
        >
          {errorMessage}
        </Code>
      )}
    </Alert>
  );
};
